import { setLanguageForExpressions } from '@lmt/expressions';
import { AppLanguage } from '@lmt/unicorn-shared/app/constants/languages';
import { MaybeUndefined } from '@lmt/unicorn-shared/app/helpers/typeUtils';
import {
  addHours,
  addMilliseconds,
  addSeconds,
  differenceInHours,
  differenceInMilliseconds,
  format,
  Interval,
  startOfDay,
  startOfToday,
} from 'date-fns';
import { de, enGB, enUS } from 'date-fns/locale';
import i18next from 'i18next';
import Logger from 'js-logger';
import { useTranslation } from 'react-i18next';
import { assertNeverDontThrow } from './assert-never';
import {
  convertFromCurrentTimezone,
  defaultToISOString,
} from './timezone-change-helper';
import { DateTimeParam } from 'react-router-url-params';
import { deDE as muiDeDe, enUS as muiEnUs } from '@mui/material/locale';
import {
  deDE as muiPickersDeDe,
  enUS as muiPickersEnUs,
} from '@mui/x-date-pickers/locales';
import {
  locale as setDevExpressLocale,
  loadMessages,
} from 'devextreme/localization';
import deMessages from 'devextreme/localization/messages/de.json';
import enMessages from 'devextreme/localization/messages/en.json';
import deRichEdit from '@lmt/unicorn-shared/app/localization/dx-rich.de.json';

export function getDayTimeInMillis(
  dateTime: Date | undefined | number,
): number | undefined {
  return dateTime && differenceInMilliseconds(dateTime, startOfDay(dateTime));
}

// round up date minutes to the closest step (e.g. if step is 30 minutes, 10:12 -> 10:30, 11:44 -> 12:00)
export function roundUpMinutes(date: Date, stepInMinutes: number): number {
  return date.setMinutes(
    Math.ceil(date.getMinutes() / stepInMinutes) * stepInMinutes,
    0,
    0,
  );
}

export function getDateTimeInterval(interval: Interval | undefined) {
  if (interval) {
    return {
      startDate: startOfDay(interval.start),
      startTimeInMills: getDayTimeInMillis(interval.start),
      endDate: startOfDay(interval.end),
      endTimeInMills: getDayTimeInMillis(interval.end),
    };
  }
  return {
    startDate: startOfDay(new Date()),
    startTimeInMills: getDayTimeInMillis(roundUpMinutes(new Date(), 30)),
    endDate: startOfDay(roundUpMinutes(addHours(new Date(), 1), 30)),
    endTimeInMills: getDayTimeInMillis(
      roundUpMinutes(addHours(new Date(), 1), 30),
    ),
  };
}

export function formatDurationInSecondsAsHHMM(
  durationInSeconds: number,
): string {
  const startDate = startOfToday();
  const endDate = addSeconds(startOfToday(), durationInSeconds);
  return `${differenceInHours(startDate, endDate)}:${format(endDate, 'mm')}`;
}

export function secondsToTimeString<T extends number | undefined | null>(
  timeInSeconds: T,
): string | MaybeUndefined<T> {
  if (timeInSeconds === null) return null as MaybeUndefined<T>;
  if (timeInSeconds === undefined) return undefined as MaybeUndefined<T>;

  return localFormat(addSeconds(startOfToday(), timeInSeconds), 'p');
}

let _locale: Locale = enGB;

export function localFormat(date: Date, dateFormat: string): string {
  if (!date) return '';
  return format(date, dateFormat, { locale: _locale });
}

export function setLanguageForLocale(language: AppLanguage) {
  _locale = getLanguageDefaultLocale(language);
  console.log(
    'setLanguage',
    language,
    _locale.code,
    localFormat(new Date(), 'Pp'),
  );
  setLanguageForExpressions(language).catch((e) => Logger.error(e));
  loadMessages(getDevexpressMessages(language));
  setDevExpressLocale(language === 'en' ? 'en-GB' : language);
}

function getLanguageDefaultLocale(language: AppLanguage) {
  switch (language) {
    case AppLanguage.en:
      return enGB;
    case AppLanguage['en-us']:
      return enUS;
    case AppLanguage.de:
      return de;

    default:
      assertNeverDontThrow(language);
      return enGB;
  }
}

function getDevexpressMessages(language: AppLanguage) {
  switch (language) {
    case AppLanguage.en:
    case AppLanguage['en-us']:
      return enMessages;

    case AppLanguage.de:
      deMessages.de = {
        ...deMessages.de,
        ...deRichEdit,
      };
      return deMessages;

    default:
      assertNeverDontThrow(language);
      return enMessages;
  }
}

export function getCurrentMuiDatePickersLocale() {
  const language = i18next.language as AppLanguage;
  switch (language) {
    case AppLanguage.en:
      return muiEnUs;
    case AppLanguage['en-us']:
      return muiEnUs;
    case AppLanguage.de:
      return muiDeDe;

    default:
      assertNeverDontThrow(language);
      return muiEnUs;
  }
}
export function getCurrentMuiDatePickersLocaleText() {
  const language = i18next.language as AppLanguage;
  switch (language) {
    case AppLanguage.en:
      return muiPickersEnUs;
    case AppLanguage['en-us']:
      return muiPickersEnUs;
    case AppLanguage.de:
      return muiPickersDeDe;

    default:
      assertNeverDontThrow(language);
      return muiPickersEnUs;
  }
}

export function setLanguage(language: AppLanguage) {
  setLanguageForLocale(language);
  i18next
    .changeLanguage(language === 'en' ? 'en-GB' : language)
    .catch((e) => Logger.error(e));
}

export function useLocale() {
  // to cause rerender on language change
  useTranslation();
  return _locale;
}

export const TimeUnitValues = {
  years: 3600 * 24 * 365,
  months: 3600 * 24 * 30,
  weeks: 3600 * 24 * 7,
  days: 3600 * 24,
} as const;
export const TimeUnitsSortedDescending: TimeUnit[] = [
  'years',
  'months',
  'weeks',
  'days',
];
export const TimeUnitsSortedAscending: TimeUnit[] = [
  'days',
  'weeks',
  'months',
  'years',
];

export type TimeUnit = keyof typeof TimeUnitValues;

export const TimeDirections = ['future', 'past'] as const;

export type TimeDirection = (typeof TimeDirections)[number];

export const DateTimeParamUnicorn = {
  ...DateTimeParam,
  encode: (input: Date) => {
    if (!input) return null;
    return defaultToISOString(convertFromCurrentTimezone(input));
  },
  decode: (input: any) => {
    const result = DateTimeParam.decode(input);
    return result;
  },
};

export function convertLocalDateToUTC(localDate: Date) {
  return new Date(
    Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localDate.getHours(),
      localDate.getMinutes(),
      localDate.getSeconds(),
    ),
  );
}
